import {
  Recipient,
  SharedRecipient,
  SingleStudentRecipient,
  SingleTeacherRecipient,
} from '../../pages/home/compose/composeTypes'
import { makeRequest, requestOrUndefined } from '../../request'
import { Toasts } from '../../providers/toast'
import { BasicUser } from './users'

const loadUsersByIdentifiers =
  <T extends object>(fetchType: string, simple: boolean = false, limitSchoolIDs?: number[]) =>
  (identifiers: string[]) =>
    makeRequest<(T | { missing: true; id: string })[]>('/frontend_api/fetchRecipientsByIdentifiers', {
      identifiers,
      fetchType,
      simple,
      limitSchoolIDs,
    }).then(async (response) => {
      const missing = response.filter((r) => 'missing' in r)
      if (missing.length > 0)
        await Toasts.showToast({
          subject: `Error Loading ${missing.length} Recipients`,
          message: `Could not find a recipient for ${missing.map((r) => `'${r.id}'`).join(', ')}.`,
          error: true,
          duration: 60000,
        })
      return response.filter((r) => !('missing' in r)) as T[]
    })

export const loadRecipientsByIdentifiers = (fetchType: 'student' | 'teacher') =>
  loadUsersByIdentifiers<SingleStudentRecipient | SingleTeacherRecipient>(fetchType)

export const loadStudentsByIdentifiers = (limitSchoolIDs?: number[]) =>
  loadUsersByIdentifiers<SingleStudentRecipient>('student', false, limitSchoolIDs)

export const loadSharedRecipientsByIdentifiers = () => loadUsersByIdentifiers<SharedRecipient>('teacherShare')

export const loadBasicUsersByIdentifiers = (fetchType: 'student' | 'teacher') => (identifiers: string[]) =>
  loadUsersByIdentifiers<BasicUser>(fetchType, true)(identifiers)

export const loadRecipientsImmediately = async (
  alreadyLoadedRecipients: Recipient[],
  userIDs: number[],
  groupIDs: (number | string)[]
) => {
  const existing = alreadyLoadedRecipients.filter((r) =>
    r.group ? groupIDs.includes(r.groupID) : userIDs.includes(r.id)
  )
  const toLoad = [
    ...userIDs.filter((id) => !existing.some((r) => !r.group && r.id === id)).map((id) => ({ id })),
    ...groupIDs.filter((id) => !existing.some((r) => r.group && r.groupID === id)).map((id) => ({ groupID: id })),
  ]
  if (toLoad.length === 0) return existing
  const loaded = await requestOrUndefined<Recipient[]>('/frontend_api/fetchRecipientsByIDs', {
    recipients: toLoad,
  })
  if (loaded?.length !== toLoad.length)
    Toasts.showToast({
      subject: 'Error Loading Recipients',
      message: `Could not find ${toLoad.length - (loaded?.length || 0)} recipients`,
      error: true,
    })
  return [...existing, ...(loaded || [])]
}

export const loadSharedRecipientsImmediately = async (
  alreadyLoadedSharedRecipients: SharedRecipient[],
  ids: (number | string)[]
) => {
  const existing = alreadyLoadedSharedRecipients.filter((r) => ids.includes(r.group ? r.groupID : r.id))
  const toLoad = ids.filter((id) => !existing.some((r) => (r.group ? r.groupID === id : r.id === id)))
  const loaded = await requestOrUndefined<SharedRecipient[]>('/frontend_api/fetchSharedTeachersByIDs', {
    recipients: toLoad,
  })
  return [...existing, ...(loaded || [])]
}
